import { __extends } from "tslib";
import { ASObject } from '@awayfl/avm2';
import { ObjectUtilities, release } from '@awayfl/swf-loader';
import { assert, axCoerceString } from '@awayjs/graphics';
var Event = /** @class */ (function (_super) {
    __extends(Event, _super);
    function Event(type, bubbles, cancelable) {
        if (bubbles === void 0) { bubbles = true; }
        if (cancelable === void 0) { cancelable = true; }
        var _this = _super.call(this) || this;
        //	todo:
        //	these 2 only exists to prevent errors in types for now, because for some reason
        //	some functions use core/EventBase for type but recieve as3/Event
        _this._iAllowedToPropagate = true;
        _this._iAllowedToImmediatlyPropagate = true;
        _this._type = axCoerceString(type);
        _this._bubbles = !!bubbles;
        _this._cancelable = !!cancelable;
        _this._target = null;
        _this._currentTarget = null;
        _this._eventPhase = null; //80pro: todo: EventPhase.AT_TARGET;
        _this._stopPropagation = false;
        _this._stopImmediatePropagation = false;
        _this._isDefaultPrevented = false;
        return _this;
    }
    Event.getInstance = function (type, bubbles, cancelable) {
        if (bubbles === void 0) { bubbles = false; }
        if (cancelable === void 0) { cancelable = false; }
        var instance = this._instances[type];
        if (!instance) {
            instance = new Event(type, bubbles, cancelable);
            this._instances[type] = instance;
        }
        instance._bubbles = bubbles;
        instance._cancelable = cancelable;
        return instance;
    };
    Event.getBroadcastInstance = function (type, bubbles, cancelable) {
        if (bubbles === void 0) { bubbles = false; }
        if (cancelable === void 0) { cancelable = false; }
        var instance = this._instances[type];
        if (!instance) {
            instance = new Event(type, bubbles, cancelable);
            this._instances[type] = instance;
            // Some events are documented as broadcast event in the AS3 docs.
            // We can't set |_isBroadcastEvent| flag in the
            // constructor because if you create custom events with these types they do capture and bubble.
            release || assert(Event.isBroadcastEventType(type));
        }
        instance._isBroadcastEvent = true;
        instance._bubbles = bubbles;
        instance._cancelable = cancelable;
        return instance;
    };
    /**
   * http://stackoverflow.com/questions/16900176/as3enterframe-event-propagation-understanding-issue
   */
    Event.isBroadcastEventType = function (type) {
        switch (type) {
            case Event.ENTER_FRAME:
            case Event.EXIT_FRAME:
            case Event.FRAME_CONSTRUCTED:
            case Event.RENDER:
            case Event.ACTIVATE:
            case Event.DEACTIVATE:
                return true;
        }
        return false;
    };
    Object.defineProperty(Event.prototype, "type", {
        get: function () {
            return this._type;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event.prototype, "bubbles", {
        get: function () {
            return this._bubbles;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event.prototype, "cancelable", {
        get: function () {
            return this._cancelable;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event.prototype, "target", {
        get: function () {
            return this._target;
        },
        set: function (value) {
            this._target = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event.prototype, "currentTarget", {
        get: function () {
            return this._currentTarget;
        },
        set: function (value) {
            this._currentTarget = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Event.prototype, "eventPhase", {
        get: function () {
            return this._eventPhase;
        },
        enumerable: true,
        configurable: true
    });
    Event.prototype.stopPropagation = function () {
        this._stopPropagation = true;
        if (this.adaptee) {
            this.adaptee.stopPropagation();
        }
    };
    Event.prototype.stopImmediatePropagation = function () {
        this._stopImmediatePropagation = this._stopPropagation = true;
        if (this.adaptee) {
            this.adaptee.stopImmediatePropagation();
        }
    };
    Event.prototype.preventDefault = function () {
        if (this._cancelable) {
            this._isDefaultPrevented = true;
        }
    };
    Event.prototype.isDefaultPrevented = function () {
        return this._isDefaultPrevented;
    };
    Event.prototype.isBroadcastEvent = function () {
        return !!this._isBroadcastEvent;
    };
    Event.prototype.clone = function () {
        return new Event(this._type, this._bubbles, this._cancelable);
    };
    Event.prototype.toString = function () {
        return this.formatToString('Event', 'type', 'bubbles', 'cancelable', 'eventPhase');
    };
    Event.prototype.formatToString = function (className) {
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        var str = '[' + className;
        for (var i = 0; i < args.length; i++) {
            var field = args[i];
            var value = this.axGetPublicProperty(field);
            if (typeof value === 'string') {
                value = '"' + value + '"';
            }
            str += ' ' + field + '=' + value;
        }
        return str + ']';
    };
    Event.classInitializer = function () {
        var self = this;
        self._instances = ObjectUtilities.createMap();
    };
    // JS -> AS Bindings
    Event.ACTIVATE = 'activate';
    Event.ADDED = 'added';
    Event.ADDED_TO_STAGE = 'addedToStage';
    Event.CANCEL = 'cancel';
    Event.CHANGE = 'change';
    Event.CLEAR = 'clear';
    Event.CLOSE = 'close';
    Event.COMPLETE = 'complete';
    Event.CONNECT = 'connect';
    Event.COPY = 'copy';
    Event.CUT = 'cut';
    Event.DEACTIVATE = 'deactivate';
    Event.ENTER_FRAME = 'enterFrame';
    Event.FRAME_CONSTRUCTED = 'frameConstructed';
    Event.EXIT_FRAME = 'exitFrame';
    Event.FRAME_LABEL = 'frameLabel';
    Event.ID3 = 'id3';
    Event.INIT = 'init';
    Event.MOUSE_LEAVE = 'mouseLeave';
    Event.OPEN = 'open';
    Event.PASTE = 'paste';
    Event.REMOVED = 'removed';
    Event.REMOVED_FROM_STAGE = 'removedFromStage';
    Event.RENDER = 'render';
    Event.RESIZE = 'resize';
    Event.SCROLL = 'scroll';
    Event.TEXT_INTERACTION_MODE_CHANGE = 'textInteractionModeChange';
    Event.SELECT = 'select';
    Event.SELECT_ALL = 'selectAll';
    Event.SOUND_COMPLETE = 'soundComplete';
    Event.TAB_CHILDREN_CHANGE = 'tabChildrenChange';
    Event.TAB_ENABLED_CHANGE = 'tabEnabledChange';
    Event.TAB_INDEX_CHANGE = 'tabIndexChange';
    Event.UNLOAD = 'unload';
    Event.FULLSCREEN = 'fullScreen';
    Event.CONTEXT3D_CREATE = 'context3DCreate';
    Event.TEXTURE_READY = 'textureReady';
    Event.VIDEO_FRAME = 'videoFrame';
    Event.SUSPEND = 'suspend';
    Event.AVM1_INIT = 'initialize';
    Event.AVM1_CONSTRUCT = 'construct';
    Event.AVM1_LOAD = 'load';
    return Event;
}(ASObject));
export { Event };
